import { AlignLeft, Check, ListChecks, Shuffle, TextCursorInput } from 'lucide-react';
import type { AuthoringQuestionType } from '../../../school_panel/api/types';

export const TYPE_LABEL: Record<AuthoringQuestionType, string> = {
  multiple_choice: 'Multiple choice',
  fill_blank: 'Fill in the blank',
  matching: 'Matching',
  written: 'Written answer',
};

const TYPE_HINT: Record<AuthoringQuestionType, string> = {
  multiple_choice: 'Four choices, one correct. Marked automatically.',
  fill_blank: 'A sentence with gaps and their accepted answers.',
  matching: 'Pair items on the left with items on the right.',
  written: 'Students write an answer; AI suggests marks, you confirm.',
};

export const TYPE_ICON: Record<AuthoringQuestionType, JSX.Element> = {
  multiple_choice: <ListChecks size={18} aria-hidden />,
  fill_blank: <TextCursorInput size={18} aria-hidden />,
  matching: <Shuffle size={18} aria-hidden />,
  written: <AlignLeft size={18} aria-hidden />,
};

export default function QuestionTypePicker({ onPick, compact = false }: { onPick: (t: AuthoringQuestionType) => void; compact?: boolean }) {
  const types = Object.keys(TYPE_LABEL) as AuthoringQuestionType[];
  if (compact) {
    return (
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Add a question">
        {types.map((t) => (
          <button key={t} type="button" aria-label={TYPE_LABEL[t]} onClick={() => onPick(t)}
            className="inline-flex min-h-[34px] items-center gap-1.5 rounded-[10px] border border-line-strong bg-surface px-2.5 text-xs font-semibold text-ink hover:border-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            {TYPE_ICON[t]}{TYPE_LABEL[t]}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" role="group" aria-label="Add a question">
      {types.map((t) => (
        <button key={t} type="button" aria-label={TYPE_LABEL[t]} onClick={() => onPick(t)}
          className="group flex items-start gap-3 rounded-[16px] border border-line bg-surface p-4 text-left transition hover:-translate-y-px hover:border-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
          <span className="grid h-10 w-10 flex-none place-items-center rounded-[12px] bg-mist text-navy dark:text-ink">{TYPE_ICON[t]}</span>
          <span className="min-w-0">
            <span className="block font-semibold text-ink">{TYPE_LABEL[t]}</span>
            <span className="mt-0.5 block text-[13px] text-muted">{TYPE_HINT[t]}</span>
          </span>
          <Check size={16} aria-hidden className="ml-auto mt-1 flex-none text-transparent group-hover:text-muted" />
        </button>
      ))}
    </div>
  );
}
